import { useQuery, type QueryClient } from '@tanstack/react-query'
import { api } from './api'

/**
 * Workspace-Scoped JWT Service — ADR-024 β-prime consumer
 *
 * admin-frontend mints a short-lived (15 min) workspace-scoped JWT from
 * admin-service then uses it to call api-gateway directly as
 * role=platform_admin scoped to one workspaceId. This is the FIRST
 * foundational consumer of Block 3a's `POST /admin/api/v1/auth/workspace-token`
 * endpoint and sets the template for FEAT-104 → FEAT-110.
 *
 * Design notes:
 * - Tokens are cached in TanStack Query (NOT localStorage / cookies — they
 *   never leave React memory; matches admin-session JWT discipline).
 * - staleTime = 14 min, gcTime = 15 min: the cache invalidates 1 minute
 *   before the JWT expires so any in-flight axios request that calls
 *   `ensureWorkspaceToken()` triggers a silent re-mint instead of hitting
 *   401 from a stale token.
 * - Multiple consumers (analytics, future workflow, etc.) for the same
 *   workspaceId share the same cache entry — the mint endpoint is only
 *   called once per 14 min per workspace.
 */

export interface WorkspaceTokenResponse {
  /** The minted JWT — pass via `Authorization: Bearer <token>` to api-gateway. */
  token: string
  /** ISO 8601 expiration timestamp. */
  expiresAt: string
}

const TOKEN_TTL_SECONDS = 15 * 60
const TOKEN_STALE_SECONDS = 14 * 60 // refresh 1 minute before expiry

export const WORKSPACE_TOKEN_QUERY_KEY_PREFIX = 'workspaceToken' as const

export function workspaceTokenQueryKey(
  workspaceId: string
): readonly [typeof WORKSPACE_TOKEN_QUERY_KEY_PREFIX, string] {
  return [WORKSPACE_TOKEN_QUERY_KEY_PREFIX, workspaceId] as const
}

/**
 * Low-level mint call. Hits admin-service via the existing axios admin
 * client (which already attaches the admin-session bearer + refresh-cookie).
 */
export async function mintWorkspaceToken(
  workspaceId: string
): Promise<WorkspaceTokenResponse> {
  const response = await api.post<WorkspaceTokenResponse>(
    '/auth/workspace-token',
    { workspaceId }
  )
  return response.data
}

/**
 * React hook: returns the current workspace-scoped JWT for `workspaceId`.
 * Caches in TanStack Query, refreshes 1 min before expiry, returns null
 * when no workspaceId is set.
 */
export function useWorkspaceToken(workspaceId: string | undefined) {
  return useQuery({
    queryKey: workspaceId
      ? workspaceTokenQueryKey(workspaceId)
      : [WORKSPACE_TOKEN_QUERY_KEY_PREFIX, '__none__'],
    queryFn: () => mintWorkspaceToken(workspaceId!),
    enabled: !!workspaceId,
    staleTime: TOKEN_STALE_SECONDS * 1000,
    gcTime: TOKEN_TTL_SECONDS * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: TOKEN_STALE_SECONDS * 1000,
    retry: 1,
  })
}

/**
 * Imperative accessor used by the apiGateway axios interceptor.
 *
 * `ensureQueryData` is the key choice — it awaits an in-flight refetch
 * instead of reading-from-cache-synchronously. If the token is stale or
 * missing, the interceptor blocks until a fresh mint completes, so
 * downstream requests never carry an expired JWT.
 */
export function ensureWorkspaceToken(
  queryClient: QueryClient,
  workspaceId: string
): Promise<WorkspaceTokenResponse> {
  return queryClient.ensureQueryData({
    queryKey: workspaceTokenQueryKey(workspaceId),
    queryFn: () => mintWorkspaceToken(workspaceId),
    staleTime: TOKEN_STALE_SECONDS * 1000,
  })
}

/**
 * Call from auth.store.logout() to forget all workspace tokens on sign-out.
 */
export function clearAllWorkspaceTokens(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: [WORKSPACE_TOKEN_QUERY_KEY_PREFIX] })
}
